import {
  Button,
  Grid,
  TextField,
  TextFieldProps,
  Typography,
} from '@material-ui/core';
import React from 'react';
import { Autocomplete } from '@material-ui/lab';
import ActionsButtonsBaseModal from 'components/atoms/ActionsButtonsBaseModal';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import ConfirmModal from 'components/atoms/ConfirmModal';
import { useFormik } from 'formik';
import { getInstallationFilter } from 'services/installation';
import { useQuery } from 'react-query';
import { IFilterOptions } from 'interfaces/SelectOptions';
import { useSelectFilter } from 'hooks/planning/useFilter';
import * as Yup from 'yup';
import { getWarehouseId, postMoveItems } from 'services/stock-position';
import LoadingInputEndAdornment from 'components/atoms/LoadingInputEndAdornment';
import useStyles from './style';
import { IModalTransferProps, IOption } from './types';

const FORM_ID = 'TRANSFER_FORM_MODAL';

const DEFAULT_HELPER_TEXT = 'Obrigatório';
const DEFAULT_ERROR_TEXT = 'Preenchimento inválido.';
const DEFAULT_ERROR_TEXT_LOCAL = 'Ultrapassou o limite de 200 caracteres';
const NO_OPTIONS_TEXT = 'Nenhum resultado encontrado';

const ModalTransfer = ({
  children,
  open,
  onClose,
  gridRow,
  reloadData,
}: IModalTransferProps) => {
  const classes = useStyles();

  const [loadingSaveForm, setLoadingSaveForm] = React.useState<boolean>(false);
  const [openConfirmModal, setOpenConfirmModal] = React.useState(false);

  const [filterInstallationOptions, setFilterInstallationOptions] =
    React.useState<IFilterOptions[]>([]);

  const [installationFilterOptionsContext] = [useSelectFilter('installation')];
  const [warehouseOptions, setWarehouseOptions] = React.useState<IOption[]>([]);

  const validationSchema = Yup.object({
    installationId: Yup.string().required(DEFAULT_HELPER_TEXT),
    quantity: Yup.number()
      .positive(DEFAULT_ERROR_TEXT)
      .max(Number(gridRow?.currentQuantity), DEFAULT_ERROR_TEXT)
      .required(DEFAULT_HELPER_TEXT)
      .typeError(DEFAULT_ERROR_TEXT),
    warehouseId: Yup.string().required(DEFAULT_HELPER_TEXT),
    local: Yup.string().max(200, DEFAULT_ERROR_TEXT_LOCAL),
  });

  const formik = useFormik({
    initialValues: {
      installationId: '',
      quantity: 0,
      warehouseId: '',
      local: '',
    },
    validationSchema,
    onSubmit: formValues => {
      const stockItemId = gridRow?.id;
      setLoadingSaveForm(true);
      postMoveItems({ ...formValues, stockItemId })
        .then(() => {
          if (reloadData) reloadData();
          onClose();
        })
        .finally(() => setLoadingSaveForm(false));
    },
  });

  const enableQueries = React.useMemo(() => open, [open]);

  const pauseFilterInstallation = React.useMemo<boolean>(() => {
    return enableQueries && installationFilterOptionsContext.length > 0;
  }, [enableQueries, installationFilterOptionsContext.length]);

  const queryFilterInstallation = useQuery({
    queryKey: ['installationFilterOptionsContext'],
    queryFn: async () => {
      const { data } = await getInstallationFilter();
      return data;
    },
    select: data => {
      return data.map(item => {
        return {
          label: item?.description,
          value: item?.id,
        };
      });
    },
    onSuccess: (data: IFilterOptions[]) => {
      setFilterInstallationOptions(data);
    },
    enabled: !pauseFilterInstallation,
  });

  const enableWareHouseOptions = React.useMemo(
    () => enableQueries && Boolean(formik?.values.installationId),
    [enableQueries, formik?.values.installationId],
  );

  const queryFilterWarehouse = useQuery({
    queryKey: ['warehouseFilterContext', [formik?.values.installationId]],
    queryFn: async () => {
      const id = formik.values?.installationId;
      const { data } = await getWarehouseId(id);
      return data;
    },
    select: data => {
      return data.map(option => {
        return {
          label: option.description,
          value: option.id,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setWarehouseOptions(data);
    },
    enabled: enableWareHouseOptions,
  });

  const handleAutoCompleteChange = React.useCallback(
    (name: string, option: IOption) => {
      const value = option?.value || '';
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  const onCloseModal = React.useCallback(() => {
    if (formik.dirty) {
      setOpenConfirmModal(true);
    } else {
      onClose();
      formik.resetForm();
    }
  }, [formik, onClose]);

  const inputRenderer = React.useCallback(
    (params: TextFieldProps, loading?: boolean) => (
      <TextField
        {...params}
        className={classes.formBaseInput}
        variant="outlined"
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              <LoadingInputEndAdornment loading={loading} />
              {params?.InputProps?.endAdornment}
            </>
          ),
        }}
      />
    ),
    [classes.formBaseInput],
  );

  React.useEffect(() => {
    if (formik.dirty && !open) {
      formik.resetForm();
    }
  }, [formik, open]);

  const renderBody = React.useCallback(() => {
    return (
      <form
        id={FORM_ID}
        className={classes.bodyContent}
        onSubmit={formik.handleSubmit}
      >
        <Typography className={classes.formMessage}>
          Selecione a instalação para qual o item será transferido:
        </Typography>
        <Grid item xs={12}>
          <Autocomplete
            id="installationId"
            options={filterInstallationOptions}
            getOptionLabel={option => option.label}
            className={classes.inputList}
            onChange={(e, selected) => {
              formik.setValues(oldValues => ({
                ...oldValues,
              }));

              return handleAutoCompleteChange(
                'installationId',
                selected as IOption,
              );
            }}
            renderInput={params => (
              <TextField
                {...params}
                label="Instalação"
                variant="outlined"
                helperText="Obrigatório"
              />
            )}
            loading={queryFilterInstallation?.isLoading}
          />
        </Grid>
        <Typography className={classes.formMessage}>
          Informe a quantidade do item a ser transferido:
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              id="currentQuantity"
              name="currentQuantity"
              label="Qnt. Atual"
              variant="outlined"
              value={Number(gridRow?.currentQuantity)}
              onChange={formik.handleChange}
              disabled
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="quantity"
              name="quantity"
              label="Qnt. a transferir"
              variant="outlined"
              value={formik.values.quantity}
              onBlur={formik.handleBlur}
              onChange={event => {
                const { value, name } = event.target;

                if (value) {
                  formik.setFieldValue(name, Number(value));
                } else {
                  formik.setFieldValue(name, null);
                }
              }}
              helperText={
                (formik.touched.quantity && formik.errors.quantity) ||
                DEFAULT_HELPER_TEXT
              }
              inputProps={{
                type: 'number',
                min: 0,
                max: Number(gridRow?.currentQuantity),
              }}
              error={formik.touched.quantity && Boolean(formik.errors.quantity)}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Autocomplete
              id="warehouseId"
              options={warehouseOptions}
              disabled={!formik.values.installationId}
              getOptionLabel={option => option.label}
              noOptionsText={NO_OPTIONS_TEXT}
              className={classes.inputList}
              onChange={(e, selected) => {
                formik.setValues(oldValues => ({
                  ...oldValues,
                  warehouseId: '',
                }));

                return handleAutoCompleteChange(
                  'warehouseId',
                  selected as IOption,
                );
              }}
              renderInput={params => {
                const extraProps = {
                  name: 'warehouseId',
                  label: 'Almoxarifado',
                  error:
                    formik.touched.warehouseId &&
                    Boolean(formik.errors.warehouseId),
                  helperText:
                    (formik.touched.warehouseId && formik.errors.warehouseId) ||
                    DEFAULT_HELPER_TEXT,
                };
                return inputRenderer(
                  { ...params, ...extraProps },
                  queryFilterWarehouse?.isLoading,
                );
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="local"
              name="local"
              label="Local"
              variant="outlined"
              helperText={
                (formik.touched.local && formik.errors.local) || 'Opcional'
              }
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.local && Boolean(formik.errors.local)}
            />
          </Grid>
        </Grid>
      </form>
    );
  }, [
    classes.bodyContent,
    classes.formMessage,
    classes.inputList,
    filterInstallationOptions,
    formik,
    gridRow?.currentQuantity,
    handleAutoCompleteChange,
    inputRenderer,
    queryFilterInstallation?.isLoading,
    queryFilterWarehouse?.isLoading,
    warehouseOptions,
  ]);

  const renderFooter = React.useCallback(() => {
    return (
      <div>
        <Button color="primary" onClick={onCloseModal}>
          Cancelar
        </Button>
        <ButtonWithLoading
          disabled={!formik.dirty || !formik.isValid}
          form={FORM_ID}
          type="submit"
          color="primary"
          isLoading={loadingSaveForm}
        >
          Salvar
        </ButtonWithLoading>
      </div>
    );
  }, [formik.dirty, formik.isValid, loadingSaveForm, onCloseModal]);

  return (
    <>
      <ConfirmModal
        title="Confirmar Saída"
        message="As alterações feitas não serão salvas."
        open={openConfirmModal}
        width="433px"
        onAccept={() => {
          onClose();
          setOpenConfirmModal(false);
        }}
        onDecline={() => setOpenConfirmModal(false)}
      />
      <ActionsButtonsBaseModal
        title="Transferir"
        body={renderBody()}
        footer={renderFooter()}
        open={open}
        onClose={onCloseModal}
        width="516px"
      >
        {children}
      </ActionsButtonsBaseModal>
    </>
  );
};

export default ModalTransfer;
